<template>
	<div class="patrimony-drops-container" :class="{ 'mobile': isMobile }">
        <!-- Currencies -->
        <dropdown-title-and-description
        class="patrimony-dropdown currency"
        :params="currencyDropParams"
        :options="currencyListForDropdown"
        :selectedOption="selectedCurrency"
        @clicked="updateSelectedCurrencyHandler" />
        <!-- Products -->
        <dropdown-title-and-description
        class="patrimony-dropdown"
        :params="productsDropParams"
        :options="productsListForDropdown"
        :selectedOption="selectedProduct"
        @clicked="emit('updateSelectedProduct', $event)" />
	</div>
</template>

<script setup>
import DropdownTitleAndDescription from '@/components/generic/dropdowns/titleAndDescription/TitleAndDescription'
/* Vue & Helpers */
import { ref, computed, watch, onMounted } from 'vue'
import { flagCurrenciesIconMapper } from '@/assets/scripts/mappers/flags'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['updateSelectedCurrency', 'updateSelectedProduct'])

/** props received
@selectedCurrency {string} - selected currency in our picker
@selectedProduct {string} - selected product in our picker
@isMobile {boolean}
*/
const props = defineProps({
    selectedCurrency: String,
    selectedProduct: String,
    isMobile: Boolean
})

/* mapActions */
const { getAccountsCurrenciesList, getAccountsProductsList } = mapGetters(moduleMapper.ACCOUNTS_PATRIMONY)
const { getAppThemeMode } = mapGetters()

/*************************************************
*                                                *
*                Currencies Dropdown             *
*                                                *
*************************************************/
const currencyDropParams = computed(() => {
    return {
        title: t('accounts.patrimony.labels.currency'),
        mobileTitle: t('accounts.patrimony.labels.currency'),
        ocultDropArrowIfOnlyOneOption: true,
        headerIcon: 'coin'
    }
})

const currencyListForDropdown = computed(() => {
    let temp = []
    /* If we have more than one currency we add an option for all currencies selection */
    if (getAccountsCurrenciesList.value.length > 1) temp.push({ id: 'allCurrencies', description: t('accounts.patrimony.labels.allCurrencies') })
    /* Then we add all currencies we have on store for our accounts */
    temp = [...temp, ...getAccountsCurrenciesList.value.map(currency => {
        return {
            ...currency,
            id: currency.name,
            specialDisplayValue: `${currency.name} - ${currency.description}`,
            description: `${currency.name} - ${currency.description}`,
            icon: `flags/${flagCurrenciesIconMapper(currency.name)}`
        }
    })]
    return temp
})

const updateSelectedCurrencyHandler = (event) => {
    emit('updateSelectedCurrency', event)
    emit('updateSelectedProduct', productsListForDropdown.value[0].id)
}

/*************************************************
*                                                *
*                  Products Dropdown             *
*                                                *
*************************************************/
const productsListForDropdown = computed(() => {
    /* For products we always have the "all products" option, even if only one product is available */
    let temp = [{ id: 'allProducts', description: t('accounts.patrimony.labels.allProducts') }]
    /* Then we add all currencies we have on store for our accounts */
    temp = [...temp, ...getAccountsProductsList.value(props.selectedCurrency, getAppThemeMode.value).map(product => {
        return {
            ...product,
            description: product.description
        }
    })]
    return temp
})

const productsDropParams = ref({
    title: t('accounts.patrimony.labels.product'),
    mobileTitle: t('accounts.patrimony.labels.product'),
    ocultDropArrowIfOnlyOneOption: true,
    headerIcon: 'briefcase'
})

watch(() => props.selectedProduct, () => {
    emit('updateSelectedTab', 'assets')
})

onMounted(() => {
    emit('updateSelectedCurrency', getAccountsCurrenciesList.value.length > 1 ? 'allCurrencies' : getAccountsCurrenciesList.value[0].name)
    emit('updateSelectedProduct', productsListForDropdown.value[0].id)
})
</script>

<style lang="scss" scoped>
.patrimony-drops-container {
    display: flex;
    flex-direction: row;
    align-items: center;

    width: 100%;
    max-width: 456px;
    height: 64px;

    padding: 16px 0;
    border-radius: 16px;

    background-color: var(--secondary-light-4);
    box-shadow: var(--box-shadow-1-inverted);

    .patrimony-dropdown {
        width: 50%;

        :deep(.dropdown-content) {
            .header {
                border-top-left-radius: 16px;
                border-bottom-left-radius: 16px;
            }

            .dropdown-animation-wrapper {
                top: 16px;
                left: 16px;
            }
        }

        &.currency {
            :deep(.dropdown-content) {
                .dropdown-animation-wrapper {
                    width: 300px;
                }
            }
        }
    }

    &:not(.mobile) {
        .patrimony-dropdown {
            &:last-of-type {
                :deep(.header) {
                    &:before {
                        content: '';

                        position: absolute;
                        left: 0;

                        width: 1px;
                        height: 36px;

                        background-color: var(--secondary-light-2);
                    }

                    border-top-right-radius: 16px;
                    border-bottom-right-radius: 16px;
                }
            }
        }
    }

    &.mobile {
        flex-direction: column;

        min-width: 100%;
        height: auto;

        margin-bottom: 16px;
        padding: 0;

        .patrimony-dropdown {
            width: 100%;

            &:first-of-type {
                border-bottom: 1px solid var(--secondary-light-2);
            }

            :deep(.dropdown-content) {
                .header {
                    border-top-right-radius: 16px;
                    border-bottom-right-radius: 16px;
                }
            }
        }
    }
}
</style>
